// 未来ボード API の失敗を、画面が扱う種類に分ける。
// apiFetchCore は、失敗した応答を "API error: <状態> <本文>" の Error にする（401 は "401_UNAUTHORIZED"）。
// サービスの本文は { error: <コード>, ...追加情報 } で、入力した本文・メモは含まれない。
import type { FutureCard } from "../../api/futureBoard";

export type FutureBoardFailureKind =
  | "unauthorized"   // ログインの有効期限切れ
  | "disabled"       // サーバーで機能が無効（FEATURE_DISABLED）
  | "not_found"      // 対象がない（他人のものも同じ応答）
  | "conflict"       // 版の競合（VERSION_CONFLICT）。current に最新のカード
  | "board_conflict" // 並びの版の競合（BOARD_CONFLICT）
  | "limit"          // 上限（カード・写真・1カードの枚数）
  | "requires_pro"   // 自分の写真は Pro のみ
  | "image_rejected" // 写真を受け付けられない（大きさ・形式・読み込み不可）
  | "busy"           // 画像の変換が混み合っている（少し待って再試行）
  | "invalid"        // 入力が不正（fields に項目名）
  | "rejected"       // 内容をサーバーが受け付けない（コードで理由を区別）
  | "ai_disabled"    // AI 支援が、サーバーで無効（AI_DISABLED）
  | "ai_limit"       // 今日の分を使い切った（AI_DAILY_LIMIT）。resetsAt に、次に使える時刻
  | "ai_wait"        // 続けて押した（AI_TOO_SOON）。retryAfterSeconds に、待つ秒数
  | "ai_busy"        // 全体が混み合っている（AI_BUSY）。回数は消費されない
  | "ai_in_progress" // すでに、ヒントを作っている（AI_IN_PROGRESS）
  | "network"        // サーバーに届かなかった
  | "server"         // サーバーの失敗
  | "unknown";

export interface FutureBoardFailure {
  kind: FutureBoardFailureKind;
  status?: number;
  /** サービスのエラーコード。例: VERSION_CONFLICT */
  code?: string;
  /** VERSION_CONFLICT のときの最新のカード */
  current?: FutureCard;
  /** INVALID_INPUT のときの、不正だった項目名 */
  fields?: string[];
  /** 上限のときの値 */
  limit?: number;
  /** BOARD_CONFLICT のときの最新の並びの版 */
  boardVersion?: number;
  /** AI_DAILY_LIMIT のときの、次に使える時刻（ISO） */
  resetsAt?: string;
  /** AI_TOO_SOON のときの、待つ秒数 */
  retryAfterSeconds?: number;
  /** 画面に出す文言の i18n キー（futureBoard.errors.*） */
  messageKey: string;
}

const NETWORK = /network request failed|failed to fetch|load failed|network error|timed out|timeout/i;

const LIMIT_CODES = new Set(["CARD_LIMIT_REACHED", "IMAGE_LIMIT_REACHED", "TOO_MANY_PENDING_IMAGES", "TOO_MANY_IMAGES"]);
const IMAGE_REJECTED_CODES = new Set(["IMAGE_TOO_LARGE", "UNSUPPORTED_IMAGE_TYPE", "UNREADABLE_IMAGE", "IMAGE_DIMENSIONS_TOO_LARGE"]);
const NOT_FOUND_CODES = new Set(["CARD_NOT_FOUND", "IMAGE_NOT_FOUND", "CHECKIN_NOT_FOUND", "AI_JOB_NOT_FOUND"]);
// ジョブの失敗の種類（GET /ai/assist/:jobId の error）と、AI 支援の受付のコード。文言は、すべて i18n にある。
const AI_CODES = ["AI_DISABLED", "AI_DAILY_LIMIT", "AI_TOO_SOON", "AI_BUSY", "AI_IN_PROGRESS",
  "AI_UNAVAILABLE", "AI_TIMEOUT", "AI_INVALID_OUTPUT", "AI_CARD_NOT_FOUND", "AI_FAILED"];
// 内容をサーバーが受け付けなかった理由（i18n にある文言だけを使う。知らないコードは unknown にする）。
const REJECTED_CODES = new Set([
  "INVALID_GOAL", "ORDER_MISMATCH", "CARD_NOT_VISIBLE", "UNKNOWN_PRESET", "PRESET_UNAVAILABLE",
  "DUPLICATE_IMAGE", "IMAGE_IN_USE", "IMAGES_UNAVAILABLE",
]);

// 画面に出す文言の i18n キーに現れうるコードの一覧（ja.json・en.json に、すべてあることをテストで確認する）。
export const FUTURE_BOARD_ERROR_CODES: readonly string[] = [
  "UNAUTHORIZED", "NETWORK", "UNKNOWN", "SERVER", "FEATURE_DISABLED", "VERSION_CONFLICT", "BOARD_CONFLICT",
  "UPLOAD_REQUIRES_PRO", "IMAGE_BUSY", "INVALID_INPUT", ...LIMIT_CODES, ...IMAGE_REJECTED_CODES, ...NOT_FOUND_CODES, ...REJECTED_CODES, ...AI_CODES,
];

const key = (code: string) => `futureBoard.errors.${code}`;

export function parseFutureBoardError(error: unknown): FutureBoardFailure {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";

  if (/401_UNAUTHORIZED/.test(message)) {
    return { kind: "unauthorized", status: 401, messageKey: key("UNAUTHORIZED") };
  }

  const match = message.match(/^API error: (\d{3})\s*([\s\S]*)$/);
  if (!match) {
    if (NETWORK.test(message)) return { kind: "network", messageKey: key("NETWORK") };
    return { kind: "unknown", messageKey: key("UNKNOWN") };
  }

  const status = Number(match[1]);
  let body: any = null;
  try {
    body = JSON.parse(match[2]);
  } catch {
    // JSON でない本文（プロキシのエラーページなど）は使わない
  }
  const code: string | undefined = typeof body?.error === "string" ? body.error : undefined;
  const base = { status, code };

  if (code === "FEATURE_DISABLED") return { ...base, kind: "disabled", messageKey: key("FEATURE_DISABLED") };
  if (code === "VERSION_CONFLICT") return { ...base, kind: "conflict", current: body.current, messageKey: key(code) };
  if (code === "BOARD_CONFLICT") return { ...base, kind: "board_conflict", boardVersion: body.board_version, messageKey: key(code) };
  if (code === "UPLOAD_REQUIRES_PRO") return { ...base, kind: "requires_pro", messageKey: key(code) };
  if (code === "AI_DISABLED") return { ...base, kind: "ai_disabled", messageKey: key(code) };
  if (code === "AI_DAILY_LIMIT") return { ...base, kind: "ai_limit", limit: typeof body.limit === "number" ? body.limit : undefined, resetsAt: typeof body.resets_at === "string" ? body.resets_at : undefined, messageKey: key(code) };
  if (code === "AI_TOO_SOON") return { ...base, kind: "ai_wait", retryAfterSeconds: typeof body.retry_after_seconds === "number" ? body.retry_after_seconds : undefined, messageKey: key(code) };
  if (code === "AI_BUSY") return { ...base, kind: "ai_busy", messageKey: key(code) };
  if (code === "AI_IN_PROGRESS") return { ...base, kind: "ai_in_progress", messageKey: key(code) };
  if (code === "IMAGE_BUSY") return { ...base, kind: "busy", messageKey: key(code) };
  if (code === "INVALID_INPUT") {
    return { ...base, kind: "invalid", fields: Array.isArray(body.fields) ? body.fields : [], messageKey: key(code) };
  }
  if (code && LIMIT_CODES.has(code)) {
    return { ...base, kind: "limit", limit: typeof body.limit === "number" ? body.limit : undefined, messageKey: key(code) };
  }
  if (code && IMAGE_REJECTED_CODES.has(code)) return { ...base, kind: "image_rejected", messageKey: key(code) };
  if (code && NOT_FOUND_CODES.has(code)) return { ...base, kind: "not_found", messageKey: key(code) };
  if (status === 404) return { ...base, kind: "not_found", messageKey: key("CARD_NOT_FOUND") };
  if (status >= 500) return { ...base, kind: "server", messageKey: key("SERVER") };
  if (code && REJECTED_CODES.has(code)) return { ...base, kind: "rejected", messageKey: key(code) };
  return { ...base, kind: "unknown", messageKey: key("UNKNOWN") };
}

// 同じ内容でもう一度送ってよい失敗か（再送は同じ client_request_id / expected_version で行う）。
export function isRetryable(failure: FutureBoardFailure): boolean {
  return failure.kind === "network" || failure.kind === "server" || failure.kind === "busy";
}

// AI トーク（「この未来について話す」）の失敗。送信前の拒否（無効・上限・実行中・カードなど）は、コードで届く。
// 生成の途中の失敗（コードなし）は、一般の失敗にする。
export interface TalkFailure {
  messageKey: string;
  /** AI_DAILY_LIMIT のときの、次に使える時刻（ISO） */
  resetsAt: string | null;
  /** サーバーで AI が無効（画面は、話す入口を隠す） */
  disabled: boolean;
}

export function talkFailure(error: unknown): TalkFailure {
  const failure = parseFutureBoardError(error);
  if (failure.kind === "unknown") return { messageKey: key("AI_FAILED"), resetsAt: null, disabled: false };
  return { messageKey: failure.messageKey, resetsAt: failure.resetsAt ?? null, disabled: failure.kind === "ai_disabled" };
}
